import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Eye, EyeOff, MessageSquare, X, ArrowLeft } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const empty = { club_id: "", title: "", body: "", image_file_id: "", status: "draft" };

export default function OwnerBlog() {
  const [clubs, setClubs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [comments, setComments] = useState([]);
  const fileRef = useRef();

  const loadPosts = () => api.get("/blog/posts/mine").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/restaurants/mine").then((r) => setClubs(r.data)).catch(() => {});
    loadPosts();
  }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, club_id: clubs[0]?.id || "" }); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm({ club_id: p.club_id, title: p.title, body: p.body, image_file_id: p.image_file_id || "", status: p.status }); setShowForm(true); };

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/blog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_file_id: data.file_id }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  const save = async (status) => {
    if (!form.club_id) return toast.error("Pick a club first.");
    if (!form.title.trim()) return toast.error("Title is required.");
    try {
      const payload = { ...form, status };
      if (editing) await api.put(`/blog/posts/${editing.id}`, payload);
      else await api.post("/blog/posts", payload);
      setShowForm(false);
      await loadPosts();
      toast.success(status === "published" ? "Post published!" : "Draft saved");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/blog/posts/${p.id}`, { club_id: p.club_id, title: p.title, body: p.body, image_file_id: p.image_file_id || "", status: p.status === "published" ? "draft" : "published" });
      await loadPosts();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const del = async (p) => {
    if (!window.confirm("Delete this post and its comments?")) return;
    try { await api.delete(`/blog/posts/${p.id}`); await loadPosts(); toast.success("Post deleted"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const viewComments = async (p) => {
    setOpenComments(p);
    const { data } = await api.get(`/blog/posts/${p.id}`);
    setComments(data.comments || []);
  };
  const delComment = async (cid) => {
    try { await api.delete(`/blog/comments/${cid}`); setComments((c) => c.filter((x) => x.id !== cid)); loadPosts(); toast.success("Comment removed"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const inputC = "w-full border border-[#17324D]/15 rounded-xl px-3 py-2 bg-white focus:outline-none";

  return (
    <div className="App bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 py-10">
        <Link to="/owner" className="inline-flex items-center gap-1 text-sm font-semibold text-[#17324D]/60 hover:text-[#D94A3D] mb-4"><ArrowLeft size={15} /> Back to dashboard</Link>
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Blog</h1>
          <button data-testid="new-post-btn" onClick={openNew}
            className="flex items-center gap-1 px-4 py-2 bg-[#D94A3D] text-white font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#C33E32]">
            <Plus size={16} /> New Post
          </button>
        </div>

        {clubs.length === 0 && <p className="text-[#17324D]/60 mb-4">Create a club first to start posting.</p>}

        <div className="space-y-3">
          {posts.length === 0 ? <p className="text-[#17324D]/80">No posts yet.</p> : posts.map((p) => (
            <div key={p.id} data-testid={`owner-post-${p.id}`} className="card-brut rounded-xl bg-white p-4 flex items-center gap-4">
              {p.image_url && <img src={`${BACKEND}${p.image_url}`} alt="" className="w-16 h-16 object-cover rounded-xl border border-[#17324D]/15" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{p.title}</p>
                <p className="text-xs text-[#17324D]/60">{p.club_name} · <span className={p.status === "published" ? "text-[#17324D] font-bold" : "text-[#17324D]/50"}>{p.status}</span> · {p.comment_count} comment{p.comment_count === 1 ? "" : "s"}</p>
              </div>
              <button data-testid={`post-comments-${p.id}`} onClick={() => viewComments(p)} title="Comments" className="p-2 border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]"><MessageSquare size={15} /></button>
              <button data-testid={`post-publish-${p.id}`} onClick={() => togglePublish(p)} title={p.status === "published" ? "Unpublish" : "Publish"} className="p-2 border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]">{p.status === "published" ? <EyeOff size={15} /> : <Eye size={15} />}</button>
              <button data-testid={`post-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]"><Pencil size={15} /></button>
              <button data-testid={`post-delete-${p.id}`} onClick={() => del(p)} className="p-2 border border-[#17324D]/15 rounded-xl hover:bg-[#D94A3D] hover:text-white"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Post form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="card-brut-lg rounded-xl bg-[#FFF4E6] w-full max-w-lg max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-xl tracking-tight">{editing ? "Edit Post" : "New Post"}</h2>
              <button onClick={() => setShowForm(false)}><X size={22} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-semibold">Club</label>
                <select data-testid="post-club" value={form.club_id} onChange={(e) => setForm({ ...form, club_id: e.target.value })} className={inputC}>
                  <option value="">Select a club…</option>
                  {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label className="text-sm font-semibold">Title</label><input data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputC} /></div>
              <div><label className="text-sm font-semibold">Body</label><textarea data-testid="post-body" rows="6" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputC} /></div>
              <div>
                <label className="text-sm font-semibold">Image (optional)</label>
                <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="post-image-input" />
                <div className="flex items-center gap-3 mt-1">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-1 px-3 py-2 border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6] text-sm disabled:opacity-60">
                    <Upload size={15} /> {uploading ? "Uploading…" : "Upload image"}
                  </button>
                  {form.image_file_id && <img src={`${BACKEND}/api/blog/image/${form.image_file_id}`} alt="preview" className="w-14 h-14 object-cover rounded-xl border border-[#17324D]/15" />}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button data-testid="save-draft-btn" onClick={() => save("draft")} className="flex-1 py-2.5 font-semibold border border-[#17324D]/15 rounded-xl hover:bg-[#FFF4E6]">Save Draft</button>
                <button data-testid="publish-btn" onClick={() => save("published")} className="flex-1 py-2.5 font-semibold bg-[#17324D] text-white border border-[#17324D]/15 rounded-xl hover:bg-[#0F2338]">Publish</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments modal */}
      {openComments && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => setOpenComments(null)}>
          <div className="card-brut-lg rounded-xl bg-[#FFF4E6] w-full max-w-lg max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-lg tracking-tight">Comments · {openComments.title}</h2>
              <button onClick={() => setOpenComments(null)}><X size={22} /></button>
            </div>
            {comments.length === 0 ? <p className="text-[#17324D]/60">No comments.</p> : (
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.id} data-testid={`owner-comment-${c.id}`} className="border border-[#17324D]/15 rounded-xl p-3 bg-white flex justify-between gap-3">
                    <div><p className="font-semibold text-sm">{c.author_name} <span className="text-xs text-[#17324D]/50 font-normal">{new Date(c.created_at).toLocaleString()}</span></p><p className="text-sm text-[#17324D]/80">{c.body}</p></div>
                    <button data-testid={`delete-comment-${c.id}`} onClick={() => delComment(c.id)} className="p-2 h-fit border border-[#17324D]/15 rounded-xl hover:bg-[#D94A3D] hover:text-white"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
